import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { uiState } from 'react-redux-ui-state';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import classnames from 'classnames';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import ORTypography from 'components/ORTypography/ORTypography';
import roleDisabled from 'services/roleDisabled/index';
import BillingPageContainer from 'components/BillingPage/BillingPageContainer';
import Price from 'components/Price/PriceContainer';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import RocketProgress from '../RocketProgress/RocketProgress';
import OverviewStep from './OverviewStep/OverviewStep';

const propTypes = {
  activeStep: PropTypes.number.isRequired,
  resetSteps: PropTypes.func.isRequired,
  instanceName: PropTypes.string,
  instanceService: PropTypes.string,
  instanceVersion: PropTypes.string,
  instanceRegion: PropTypes.string,
  instanceProvider: PropTypes.string,
  instancePlan: PropTypes.shape({
    nodes: PropTypes.shape({}),
    flavorName: PropTypes.string,
  }),
  instanceAcls: PropTypes.instanceOf(Array),
  isValid: PropTypes.bool.isRequired,
  addIpsLater: PropTypes.bool,
  instanceType: PropTypes.string,
  classes: PropTypes.shape({}).isRequired,
  newInstanceData: PropTypes.shape({}).isRequired,
  redirectToInstances: PropTypes.func.isRequired,
  createInstance: PropTypes.func.isRequired,
  needsBilling: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
  trackButtonClick: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  price: PropTypes.number.isRequired,
  supportLevelName: PropTypes.string.isRequired,
};

const defaultProps = {
  instanceName: null,
  instanceService: null,
  instanceVersion: null,
  instanceRegion: null,
  instanceProvider: null,
  instancePlan: {},
  instanceAcls: [],
  addIpsLater: false,
  instanceType: null,
};

export function UnstyledRocketOverview({
  activeStep,
  resetSteps,
  instanceName,
  instanceService,
  instanceVersion,
  instanceRegion,
  instanceProvider,
  instancePlan,
  instanceAcls,
  isValid,
  addIpsLater,
  instanceType,
  classes,
  newInstanceData,
  redirectToInstances,
  trackButtonClick,
  createInstance,
  needsBilling,
  setUiState,
  open,
  userRoles,
  price,
  supportLevelName,
}) {
  const isStepComplete = (thisStep) => Boolean(activeStep > thisStep);
  const isAclComplete = () => instanceAcls.length > 0 || addIpsLater;
  const planNode = instancePlan.nodes ? instancePlan.nodes[instanceService.toLowerCase()] : null;

  const billingOpen = (instanceData) => {
    const buttonName = 'Instance Snapshot on Billing Popup';
    const data = {
      analytics: {
        eventPayload: {
          event: buttonName,
          properties: {
            instance_name: instanceData.name,
            service: instanceData.service,
            plan: instanceData.plan,
            version: instanceData.version,
            region: instanceData.region,
            provider: instanceData.provider,
            service_subtype: instanceData.serviceSubtype,
            features: instanceData.features,
          },
        },
      },
    };
    trackButtonClick(buttonName, data);
    setUiState({
      open: !open,
    });
  };

  const submitButton = (disabled = false) => (
    <SegmentClickTracker buttonName="Create Instance Submit (Desktop)">
      <Button
        className={classes.nextButton}
        variant="contained"
        color="primary"
        disabled={!isValid || disabled}
        onClick={() =>
          needsBilling
            ? billingOpen(newInstanceData)
            : createInstance(newInstanceData, redirectToInstances)
        }
      >
        Create Instance
      </Button>
    </SegmentClickTracker>
  );

  const createInstanceButton = (button, userAccess, allowedAccess) =>
    roleDisabled(userAccess, allowedAccess) ? (
      <Tooltip title="You do not have the required access for this resource. Contact your organization owner.">
        <span>{button(true)}</span>
      </Tooltip>
    ) : (
      button()
    );

  const discount = supportLevelName === 'Promotional_20_Percent' ? 20 : 0;
  const discountCalculation = ((price - price * (1 - discount / 100)) / 100).toFixed(2);

  const promotionalTextBuilder = (primaryText, number, additionalText) =>
    discount > 0 && (
      <div className={classes.promotionalText}>
        <ORTypography className={classes.promotionalTextDetail} color="secondary">
          {primaryText}
        </ORTypography>
        {number}
        <span>{additionalText}</span>
      </div>
    );

  const promoIcon = discount > 0 && (
    <Tooltip arrow title="New Customer Promotion: 20% off for 12 months" placement="top">
      <InfoOutlinedIcon className={classes.infoIcon} fontSize="small" />
    </Tooltip>
  );

  const promotionalText = promotionalTextBuilder(
    'Promotional Pricing Discount:\u00A0',
    discount,
    '%',
  );
  const discountText = promotionalTextBuilder(
    'Discount:\u00A0',
    `-$${discountCalculation}`,
    '\u00A0/Month',
  );

  const pricingSupportText = (
    <div className={classes.supportTextContainer}>
      <ORTypography className={classes.supportText}>
        Looking for enterprise-level solutions?
      </ORTypography>
      <ORTypography className={classes.supportText}>
        <a href="mailto:sales@objectrocket.com">Contact us for a custom quote today!</a>
      </ORTypography>
    </div>
  );

  const priceInformation = (
    <div className={classes.totalRow}>
      <div className={classes.promoAndPriceContainer}>
        <div className={classes.totalRowWithPromo}>
          <div className={classes.totalLabel}>Total:&nbsp;</div>
          <Price />
          <div className={classes.totalPerMonthLabel}>&nbsp;/Month</div>
          {promoIcon}
        </div>
        {promotionalText}
        {discountText}
        <div className={classnames(classes.showWhenSmall)}>{pricingSupportText}</div>
      </div>
    </div>
  );

  return (
    <Paper square elevation={0} className={classes.resetContainer}>
      <RocketProgress activeStep={activeStep} />
      <div className={classes.showWhenBig}>
        <div className={classes.summaryCaption}>Summary:</div>
        <OverviewStep title="Step 1" isComplete={isStepComplete(0)}>
          <ul className={classes.summaryItems}>
            {instanceName && <li>{instanceName}</li>}
            {instanceService && (
              <li>
                {instanceService} {instanceVersion !== '0' && instanceVersion}
              </li>
            )}
            {instanceType && <li>{instanceType}</li>}
            {instanceProvider && (
              <li>
                {instanceProvider} {instanceRegion !== '0' && instanceRegion}
              </li>
            )}
          </ul>
        </OverviewStep>
        <OverviewStep title="Step 2" isComplete={isStepComplete(1)}>
          {planNode && (
            <ul className={classes.summaryItems}>
              <li>{instancePlan.flavorName}</li>
              <li>
                Size: {planNode.diskSizeGb * planNode.quantity}
                GB Disk/
                {(planNode.memoryMb / 1024) * planNode.quantity}
                GB RAM ({planNode.quantity} &times; {planNode.diskSizeGb}
                GB/
                {planNode.memoryMb / 1024}
                GB Nodes)
              </li>
            </ul>
          )}
        </OverviewStep>
        <OverviewStep title="Step 3" isComplete={isAclComplete()}>
          <ul className={classes.summaryItems}>
            {instanceAcls.length > 0 && <li>Whitelist IPs Added</li>}
            {addIpsLater && <li>IPs to be added later</li>}
          </ul>
        </OverviewStep>
      </div>

      <div className={classes.showWhenSmall}>
        {instanceService && <div className={classes.summaryCaption}>Summary:</div>}
        <ul className={classes.summaryItems}>
          {instanceService && (
            <li>
              {instanceService} {instanceVersion !== '0' && instanceVersion}
            </li>
          )}
          {instanceType && <li>{instanceType}</li>}
          {instanceProvider && (
            <li>
              {instanceProvider} {instanceRegion !== '0' && instanceRegion}
            </li>
          )}
          {planNode && (
            <li>
              {instancePlan.flavorName} {planNode.diskSizeGb * planNode.quantity}
              GB Disk/
              {(planNode.memoryMb / 1024) * planNode.quantity}
              GB RAM ({planNode.quantity} &times; {planNode.diskSizeGb}
              GB/
              {planNode.memoryMb / 1024}
              GB Nodes)
            </li>
          )}
        </ul>
      </div>

      <React.Fragment>
        {priceInformation}
        <div
          className={classnames(classes.actionsContainer, classes.showWhenBig, classes.actionRow)}
        >
          <Button
            onClick={resetSteps}
            className={classes.button}
            variant="contained"
            color="secondary"
          >
            Cancel
          </Button>
          {createInstanceButton(submitButton, userRoles, [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER])}
        </div>
        <div
          className={classnames(classes.actionsContainer, classes.showWhenBig, classes.actionRow)}
        >
          {pricingSupportText}
        </div>
        <BillingPageContainer
          open={open}
          billingOpen={billingOpen}
          createInstance={createInstance}
          newInstanceData={newInstanceData}
          redirectToInstances={redirectToInstances}
        />
      </React.Fragment>
    </Paper>
  );
}

UnstyledRocketOverview.propTypes = propTypes;
UnstyledRocketOverview.defaultProps = defaultProps;

const styles = (theme) => ({
  onlySteps: {
    marginBottom: theme.spacing(2),
    display: 'flex',
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  resetContainer: {
    padding: theme.spacing(3),
    left: 0,
    width: '100%',
    border: `1px solid ${theme.customColors.lightGrey}`,
  },
  connectorBasic: {
    border: 'unset',
    padding: 'unset',
    minHeight: 'unset',
  },
  nextButton: {
    marginLeft: theme.spacing(1),
    color: theme.customColors.black,
  },
  summaryCaption: {
    ...theme.typography.h6,
    color: theme.customColors.darkGrey,
    fontWeight: 800,
    paddingLeft: 7,
    [theme.breakpoints.up('md')]: {
      display: 'None',
    },
  },
  summaryItems: {
    color: theme.customColors.mediumGrey,
    paddingLeft: 35,
    fontFamily: ['"Roboto"', 'sans-serif'].join(','),
    fontSize: 12,
    fontWeight: 400,
    margin: 0,

    [theme.breakpoints.down('sm')]: {
      marginTop: '.5rem',
      marginBottom: '.5rem',
      paddingLeft: 20,
    },
  },
  stepTitle: {
    color: theme.customColors.teal,
    display: 'inline-block',
  },
  stepCheck: {
    color: theme.customColors.orange,
    display: 'inline-block',
    marginBottom: theme.spacing(-0.5),
    paddingRight: theme.spacing(0.5),
    transition: 'opacity 250ms ease-in-out',
  },
  actionRow: {
    display: 'flex',
    position: 'relative',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
    marginTop: '-3rem',

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      marginTop: 'unset',
    },
  },
  totalLabel: {
    ...theme.typography.h5,
    color: theme.customColors.mediumGrey,
    fontWeight: 500,
    fontSize: 21,
    flex: 0.01,
  },
  totalPerMonthLabel: {
    ...theme.typography.h6,
    color: theme.customColors.lightGrey,
    fontWeight: 500,
    flex: 0.01,
    alignSelf: 'center',
  },
  stepCheckInactive: {
    opacity: 0,
  },
  stepCheckActive: {
    opacity: 1,
  },
  showWhenSmall: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  showWhenBig: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  totalRowWithPromo: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      marginTop: 'unset',
    },
  },
  promoAndPriceContainer: {
    display: 'flex',
    flexFlow: 'column wrap',
  },
  promotionalText: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: theme.customColors.mediumGrey,
    fontSize: '.8571rem',

    [theme.breakpoints.up('lg')]: {
      justifyContent: 'center',
    },
  },
  promotionalTextDetail: {
    fontSize: '.8571rem',
    fontWeight: 'bold',
  },
  infoIcon: {
    marginLeft: theme.spacing(0.5),
  },
  supportTextContainer: {
    textAlign: 'center',
    marginTop: '-8px',
    marginRight: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      marginTop: 'unset',
      textAlign: 'left',
    },
  },
  supportText: {
    fontStyle: 'italic',
    fontSize: '.8571rem',
  },
});

export const uiConfig = {
  name: 'rocketOverview',
  state: () => ({
    open: false,
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledRocketOverview));
